// src/components/tutor/mobile/MobileTutorPayroll.js
import React, { useState } from "react";
import { auth, db } from "../../../firebaseConfig";
import {
  collection, query, where, getDocs, orderBy, Timestamp,
} from "firebase/firestore";
import "./MobilePayroll.css";

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtTime(ts) {
  if (!ts) return "—";
  return ts.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function MobileTutorPayroll() {
  const [loading, setLoading]   = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [records,   setRecords]   = useState([]);
  const [summary,   setSummary]   = useState({
    adminHours: 0, adminPay: 0,
    teachingHours: 0, teachingPay: 0,
  });

  // ── Generate (exact logic from TutorPayroll.js) ──────────────────────────────
  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      alert("Please select a date range.");
      return;
    }
    setLoading(true);
    try {
      const startTs = Timestamp.fromDate(new Date(`${startDate} 00:00:00`));
      const endTs   = Timestamp.fromDate(new Date(`${endDate} 23:59:59`));

      const q = query(
        collection(db, "payroll_records"),
        where("uid", "==", auth.currentUser.uid),
        where("startTime", ">=", startTs),
        where("startTime", "<=", endTs),
        orderBy("startTime", "asc"),
      );
      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRecords(data);

      let ah = 0, ap = 0, th = 0, tp = 0;
      data.forEach((r) => {
        if (r.type === "admin") { ah += r.hours; ap += r.totalPay; }
        else                    { th += r.hours; tp += r.totalPay; }
      });
      setSummary({ adminHours: ah, adminPay: ap, teachingHours: th, teachingPay: tp });
    } catch (err) {
      console.error(err);
      alert("Error generating report");
    } finally {
      setLoading(false);
    }
  };

  // ── Clear ────────────────────────────────────────────────────────────────────
  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setRecords([]);
    setSummary({ adminHours: 0, adminPay: 0, teachingHours: 0, teachingPay: 0 });
  };

  const hasResults = records.length > 0;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="mp-container">
      <div className="mp-content">

        {/* ── 1. Date range filter ── */}
        <div className="mp-filter-card">
          <div className="mp-filter-dates">
            <div className="mp-input-group">
              <label className="mp-label">From</label>
              <input
                type="date"
                className="mp-date-box"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="mp-input-group">
              <label className="mp-label">To</label>
              <input
                type="date"
                className="mp-date-box"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="mp-filter-actions">
            <button className="mp-btn-generate" onClick={handleGenerate}>
              {loading ? "…" : "Generate"}
            </button>
            <button className="mp-btn-clear" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>

        {/* ── 2. Pay summary ── */}
        <div className="mp-summary-card">
          <p className="mp-summary-title">Pay Summary</p>

          <div className="mp-summary-row">
            <span className="mp-summary-key">
              Admin
              <span className="mp-summary-hrs">{summary.adminHours.toFixed(2)} hrs</span>
            </span>
            <span className="mp-summary-val">${summary.adminPay.toFixed(2)}</span>
          </div>

          <div className="mp-summary-row">
            <span className="mp-summary-key">
              Teaching
              <span className="mp-summary-hrs">{summary.teachingHours.toFixed(2)} hrs</span>
            </span>
            <span className="mp-summary-val">${summary.teachingPay.toFixed(2)}</span>
          </div>

          <div className="mp-divider" />

          <div className="mp-total-row">
            <span className="mp-total-label">Total</span>
            <span className="mp-total-value">
              ${(summary.adminPay + summary.teachingPay).toFixed(2)}
            </span>
          </div>
        </div>

        {/* ── 3. Records list ── */}
        {hasResults ? (
          <div className="mp-records-section">
            <p className="mp-records-label">{records.length} record{records.length !== 1 ? "s" : ""}</p>
            {records.map((row) => {
              const isTeaching = row.type === "teaching";
              return (
                <div key={row.id} className="mp-record-card">
                  {/* Row 1: date + type badge */}
                  <div className="mp-record-top">
                    <span className="mp-record-date">{row.date}</span>
                    <span className={`mp-badge ${isTeaching ? "mp-badge--teaching" : "mp-badge--admin"}`}>
                      {isTeaching ? "Teaching" : "Admin"}
                    </span>
                  </div>
                  {/* Row 2: time range */}
                  <div className="mp-record-time">
                    {fmtTime(row.startTime)} → {fmtTime(row.endTime)}
                  </div>
                  {/* Row 3: hours · rate · total */}
                  <div className="mp-record-meta">
                    <span>{row.hours} hrs</span>
                    <span className="mp-record-dot">·</span>
                    <span>${row.rateUsed}/hr</span>
                    <span className="mp-record-dot">·</span>
                    <span className="mp-record-total">${row.totalPay.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="mp-empty">
              <p className="mp-empty-text">
                {startDate && endDate
                  ? "No records found for this date range."
                  : "Select a date range and tap Generate."}
              </p>
            </div>
          )
        )}

      </div>
    </div>
  );
}
